import React, { useImperativeHandle, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';

import { Handle, Modal } from '@/feature/alerts/components/Modal';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { GameResult } from '../GameResultItem';
import { GameResultModalContent } from './GameResultModalContent';

export type GameResultModalRef = {
    show: (gameResult: GameResult) => void;
};

const GAME_MATCHUP_HEIGHT = 362;
const GAME_DETAILS_HEIGHT = 498;

// The game results modal has 2 screens (GameResultMatchup and GameResultDetails)
// The user can navigate between the two screens by pressing the info icon or back icon
// The snap points represent the height of each of the two screens
const SNAP_POINTS = [GAME_MATCHUP_HEIGHT, GAME_DETAILS_HEIGHT];

export const GameResultsModal = React.forwardRef<GameResultModalRef, {}>((_props, ref) => {
    const [data, setData] = useState<GameResult | undefined>(undefined);
    const modalRef = useRef<BottomSheetModal>(null);
    const isButtonClick = useRef(false);

    useImperativeHandle(ref, () => ({
        show: modalData => {
            setData(modalData);
            modalRef.current?.present();
        },
    }));

    const onNavigationChange = (index: number) => {
        isButtonClick.current = true;
        modalRef.current?.snapToIndex(index);
    };

    const isWon = !!data && data.amount_won > 0;

    return (
        <Modal
            modalRef={modalRef}
            id={'game-result-modal'}
            handleComponent={isWon ? () => null : undefined} // Pass empty handle component, the handle is re-created in GameResultsModalWon
            modalStyle={isWon ? styles.modalWin : undefined}
            snapPoints={SNAP_POINTS}
            onAnimate={(fromIndex: number, toIndex: number) => {
                // We want to prevent the user from panning the modal to the snap points as the are only meant for each screen individually
                // And only want to allow the user to close the modal by panning down
                if (toIndex !== -1 && toIndex !== fromIndex && !isButtonClick.current) {
                    modalRef.current?.snapToIndex(fromIndex);
                }
                isButtonClick.current = false;
            }}
        >
            {data ? (
                <GameResultModalContent
                    gameResult={data}
                    onNavigationChange={onNavigationChange}
                    customHandle={isWon ? <Handle /> : undefined}
                />
            ) : null}
        </Modal>
    );
});

const styles = StyleSheet.create({
    modalWin: {
        overflow: 'hidden',
        borderTopRightRadius: 16,
        borderTopLeftRadius: 16,
    },
});
